/**
 * React shim over the shared `<simpl-impersonation-banner>` Web Component
 * from `@simpl/dev-banners` (simpl-core/packages/dev-banners). The
 * Web Component owns the visual, the CSRF handshake, and the actual
 * form submit; this shim's job is to read impersonation state from
 * the SPA's caller context and pass it in as attributes.
 *
 * The IIFE bundle is loaded via a `<script>` tag in
 * `public/index.html`, so registering the custom element is a no-op
 * side effect of that script tag and this file doesn't need to
 * import anything from `@simpl/dev-banners`. JSX types come from
 * `src/types/simpl-dev-banners.d.ts`.
 *
 * Kept in the same location as before so
 * `<SimplAuthGuard>`'s existing `<ImpersonationBanner />` mount
 * point works unchanged.
 */

import { getSimplAuthHost } from "./simplAuthSession";
import { useOptionalSimplAuthCaller } from "./SimplAuthGuard";

/**
 * Derive the QA users URL from the auth host — same pattern as the
 * previous React-native implementation. auth-<env> and
 * dashboards-<env> share `.simpl.org` in every deployed env, so
 * swapping the subdomain segment produces the right host.
 */
function deriveDashboardsQaUrl(authHost: string): string {
  try {
    const parsed = new URL(authHost);
    parsed.hostname = parsed.hostname.replace(/^auth/, "dashboards");
    parsed.pathname = "/system/qa/users/";
    return parsed.toString();
  } catch {
    return "";
  }
}

export function ImpersonationBanner(): JSX.Element | null {
  const callerCtx = useOptionalSimplAuthCaller();
  const impersonatedBy = callerCtx?.caller?.impersonatedBy;
  const impersonatedEmail = callerCtx?.caller?.email;

  // Same guard as before: without both the trailer and a target email,
  // there's nothing to show. The Web Component would also hide itself
  // in that case, but returning null keeps React's tree tidy and
  // matches the pre-Web-Component contract.
  if (!impersonatedBy || !impersonatedEmail) {
    return null;
  }

  const authHost = getSimplAuthHost();
  const exitUrl = deriveDashboardsQaUrl(authHost);

  return (
    <simpl-impersonation-banner
      impersonated-email={impersonatedEmail}
      auth-host={authHost}
      exit-url={exitUrl}
    />
  );
}
